import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { buildQueryString, exportFile, showError } from "../../../utils/utilities";
import { useFormik } from "formik";
import { get, toLower } from "lodash";
import { useTranslation } from "react-i18next";
import { langDate, langMonth } from "../../../utils/properties";
import { Col, Row, Select, DatePicker } from "antd";
import { DownloadOutlined, ClearOutlined } from "@ant-design/icons";
import Button from "../../../components/functional_components/Button/PFButton";
import ExportProject from "../../../services/api/ExportProject";
import { setLoading } from "../../../redux/ducks/Loading";
import Vertical from "../../../services/api/Vertical";
import ProjectEstimateStatus from "../../../services/api/ProjectEstimateStatus";
import dayjs from "dayjs";

const FinanceStatementProgress = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langMonth[toLower(currentLanguage)];
  const [state, setState] = useState({
    suppliersOptionsList: [],
    filterLoading: false,
    verticalsList: [],
    statusList: [],
  });

  const type = [
    {
      value: t("reportCP.filter.all"),
      id: "ALL",
    },
    {
      value: t("client.clientDetail.typeOptions.parentCompany"),
      id: "CONTROLLING_COMPANY",
    },
    {
      value: t("client.clientDetail.typeOptions.subsidiaryCompany"),
      id: "CONTROLLED_COMPANY",
    },
    {
      value: t("client.clientDetail.typeOptions.associatedCompany"),
      id: "RELATED_COMPANY",
    },
    {
      value: t("client.clientDetail.typeOptions.thirdPart"),
      id: "THIRD_PARTY",
    },
  ];

  useEffect(() => {
    getVerticalsList();
    getStatusList();
  }, []);

  const getStatusList = async () => {
    const statuses = await ProjectEstimateStatus.getStatusesList("allPages=true");
    const optionList = [];
    if (!statuses.errors && statuses.data) {
      statuses.data.forEach((status) => {
        optionList.push({ value: status.id, label: status.name });
      });
      setState((prevState) => ({
        ...prevState,
        statusList: optionList,
      }));
    } else {
      showError(get(statuses, "error.messageCode", "ER000"));
    }
  };

  const getVerticalsList = async () => {
    const verticals = await Vertical.getVerticalList("allPages=true");
    const optionList = [];
    if (!verticals.error && verticals.data) {
      verticals.data.forEach((status) => {
        optionList.push({ value: status.id, label: status.name });
      });
      setState((prevState) => ({
        ...prevState,
        verticalsList: optionList,
      }));
    } else {
      showError(get(verticals, "error.messageCode", "ER000"));
    }
  };

  const formikFilter = useFormik({
    initialValues: {
      month: "",
      vertical: [],
      status: [],
      type: "ALL",
    },
    validate: (values) => {
      let errors = {};
      if (!values.month) {
        errors.month = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      dispatch(setLoading(true));
      const obj = {
        month: dayjs(values.month, "YYYYMM").format("YYYYMM"),
        clientTypes: values.type === "ALL" ? "" : values.type,
        projectStatusEstimateIds: values.status,
        verticalIds: values.vertical,
      };
      const queryString = buildQueryString({}, obj);
      const exportResponse = await ExportProject.exportSapFinanceStatement(queryString.string);
      if (!exportResponse.error) {
        const blob = new Blob([exportResponse]);
        exportFile(blob, "project-report.csv");
      } else {
        showError(get(exportResponse, "error.messageCode", "ER000"));
      }
      dispatch(setLoading(false));
    },
  });

  return (
    <div className="filter-container">
      <Row type="flex" justify="space-between">
        <Col lg={6} md={12} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mb-10" htmlFor="month">
              {" "}
              {t("reportCP.filter.month")}*
            </label>
          </Row>
          <Row>
            <Col span={20}>
              <DatePicker
                className="w-100"
                picker="month"
                id="month"
                name="month"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                value={formikFilter.values.month}
                onChange={(date) => formikFilter.setFieldValue("month", date)}
              />
            </Col>
          </Row>
        </Col>

        <Col lg={6} md={12} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mb-10" htmlFor="type">
              {t("reportCP.filter.type")}
            </label>
          </Row>
          <Row>
            <Col span={20}>
              <Select
                placeholder={t("reportCP.filter.type")}
                id="type"
                name="type"
                className="w-100"
                onBlur={formikFilter.handleBlur("type")}
                onChange={formikFilter.handleChange("type")}
                value={formikFilter.values.type}
                options={type.map((item) => ({ value: item.id, name: item.value }))}
              />
            </Col>
          </Row>
        </Col>

        <Col lg={6} md={12} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mb-10" htmlFor="vertical">
              {t("reportCP.filter.vertical")}
            </label>
          </Row>
          <Row>
            <Col span={20}>
              <Select
                mode="multiple"
                className="w-100"
                id="vertical"
                name="vertical"
                placeholder={t("reportCP.filter.vertical")}
                onChange={(value) => formikFilter.setFieldValue("vertical", value)}
                onBlur={formikFilter.handleBlur("vertical")}
                value={formikFilter.values.vertical}
                options={state.verticalsList}
              />
            </Col>
          </Row>
        </Col>

        <Col lg={6} md={12} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mb-10" htmlFor="status">
              {t("reportCP.filter.status")}
            </label>
          </Row>
          <Row>
            <Col span={20}>
              <Select
                mode="multiple"
                id="status"
                name="status"
                placeholder={t("reportCP.filter.status")}
                className="w-100"
                onChange={(value) => formikFilter.setFieldValue("status", value)}
                onBlur={formikFilter.handleBlur("status")}
                value={formikFilter.values.status}
                options={state.statusList}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row className="mt-1em" justify="end">
        <Col>
          <Button
            isDisabled={!formikFilter.dirty}
            type="secondary"
            currentSize="large"
            clickCallback={formikFilter.resetForm}
            currentIcon={<ClearOutlined className="icon-large" />}
          />
        </Col>
        <Col className="left-margin">
          <Button
            isDisabled={!(formikFilter.dirty && formikFilter.isValid)}
            currentSize="large"
            isLoading={state.filterLoading}
            clickCallback={formikFilter.submitForm}
            currentIcon={<DownloadOutlined className="icon-large" />}
          />
        </Col>
      </Row>
    </div>
  );
};

export default FinanceStatementProgress;
